import { useState, useEffect } from "react";
import { useAppServices } from "../../../hook/services";
import { useNavigate, useParams } from "react-router-dom";

const ArticleList = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const AppService = useAppServices();
  const [processing, setProcessing] = useState(true);
  const [articles, setArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const getData = async () => {
    const { response } = await AppService.knowledgebase_article.get({
      query: `category=${categoryId}`,
    });
    if (response) {
      setArticles(response.data);
    } else {
      setArticles([]);
    }
    setProcessing(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const handleView = async (id) => {
    navigate(`./${id}`);
  };

  const formatDate = (raw) => {
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredArticles = !normalizedQuery
    ? articles
    : articles.filter((a) => {
        const name = (a.name || "").toLowerCase();
        const desc = (a.shortDescription || "").toLowerCase();
        return name.includes(normalizedQuery) || desc.includes(normalizedQuery);
      });

  return (
    <div className="kb-page">
      {/* Hero */}
      <header className="kb-hero">
        <div className="container kb-hero-inner">
          <div className="d-flex justify-content-start mb-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="kb-hero-back d-inline-flex align-items-center"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
            </button>
          </div>

          <div className="text-center">
            <span className="kb-hero-icon d-inline-flex align-items-center justify-content-center mb-3">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            <h1 className="kb-hero-title">Articles</h1>
            <p className="kb-hero-subtitle mb-3">
              Browse and manage articles in this category.
            </p>
            <span className="kb-hero-badge">
              {processing
                ? "Loading…"
                : `${articles.length} ${articles.length === 1 ? "article" : "articles"} available`}
            </span>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="container kb-toolbar">
        <div className="kb-search-wrap position-relative mx-auto w-100" style={{ maxWidth: 560 }}>
          <span className="kb-search-icon d-flex align-items-center justify-content-center" aria-hidden>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="text"
            className="kb-search-input"
            placeholder="Search articles by name or description"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="kb-search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="container kb-section">
        <div className="d-flex align-items-end justify-content-between mb-3 flex-wrap gap-2">
          <h3 className="kb-section-title mb-0">
            {normalizedQuery ? "Search results" : "All articles"}
          </h3>
          {!processing && (
            <span className="kb-section-meta">
              {filteredArticles.length}{" "}
              {filteredArticles.length === 1 ? "article" : "articles"}
            </span>
          )}
        </div>

        {processing ? (
          <div className="d-flex flex-column gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="kb-article-card-skel">
                <div className="d-flex align-items-start gap-3">
                  <div className="kb-skel rounded" style={{ width: 36, height: 36, flexShrink: 0 }} />
                  <div className="flex-grow-1">
                    <div className="kb-skel rounded" style={{ height: 12, width: "60%" }} />
                    <div className="kb-skel rounded mt-2" style={{ height: 10, width: "85%" }} />
                    <div className="kb-skel rounded mt-2" style={{ height: 10, width: "30%" }} />
                  </div>
                  <div className="kb-skel rounded" style={{ width: 32, height: 32, flexShrink: 0 }} />
                </div>
              </div>
            ))}
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="kb-empty d-flex flex-column align-items-center text-center mx-auto" style={{ maxWidth: 480 }}>
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h5 className="kb-empty-title">
              {normalizedQuery ? "No matching articles" : "No articles yet"}
            </h5>
            <p className="kb-empty-text mb-0">
              {normalizedQuery
                ? `No articles match "${searchQuery}". Try a different search term.`
                : "Articles will appear here once they're added to this category."}
            </p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {filteredArticles.map((a) => (
              <article
                key={a._id}
                className="kb-article-card"
                role="button"
                tabIndex={0}
                onClick={() => handleView(a._id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleView(a._id);
                  }
                }}
              >
                <div className="d-flex align-items-start gap-3">
                  <span className="kb-article-icon d-inline-flex align-items-center justify-content-center">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="8" y1="13" x2="16" y2="13" />
                      <line x1="8" y1="17" x2="13" y2="17" />
                    </svg>
                  </span>

                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <h6 className="kb-article-card-title mb-1 text-truncate">
                      {a.name || "Untitled article"}
                    </h6>
                    {a.shortDescription && (
                      <p className="kb-article-card-desc mb-2">
                        {a.shortDescription}
                      </p>
                    )}
                    {a.updatedAt && (
                      <div className="kb-article-card-meta d-inline-flex align-items-center">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        Updated {formatDate(a.updatedAt)}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="kb-icon-btn flex-shrink-0"
                    title="Edit article"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(a._id);
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                    </svg>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <KbStyles />
    </div>
  );
};

const KbStyles = () => (
  <style>{`
    .kb-page {
      font-family: 'Inter', sans-serif;
      background: #fafafa;
      min-height: 100vh;
    }

    /* Hero */
    .kb-hero {
      padding: 28px 16px 48px;
      background: linear-gradient(180deg, #eef2ff 0%, #f5f3ff 60%, #fafafa 100%);
      border-bottom: 1px solid #ececec;
    }
    .kb-hero-inner { max-width: 760px; margin: 0 auto; }
    .kb-hero-back {
      padding: 6px 10px;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.6);
      border: 1px solid #e0e7ff;
      color: #4338ca;
      text-decoration: none;
      cursor: pointer;
      font-family: inherit;
      transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
    }
    .kb-hero-back:hover {
      background: #fff;
      color: #4338ca;
      text-decoration: none;
    }
    .kb-hero-back:hover svg { transform: translateX(-2px); }
    .kb-hero-back svg { transition: transform 0.15s ease; }
    .kb-hero-icon {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
      color: #4f46e5;
      border: 1px solid #e0e7ff;
      box-shadow: 0 8px 24px rgba(79, 70, 229, 0.12);
    }
    .kb-hero-title {
      font-size: 1.75rem;
      font-weight: 700;
      letter-spacing: -0.01em;
      color: #0f172a;
      margin: 0 0 8px;
    }
    .kb-hero-subtitle {
      font-size: 0.9375rem;
      color: #475569;
      line-height: 1.55;
    }
    .kb-hero-badge {
      display: inline-flex;
      align-items: center;
      padding: 5px 12px;
      font-size: 0.75rem;
      font-weight: 600;
      color: #4338ca;
      background: #eef2ff;
      border: 1px solid #e0e7ff;
      border-radius: 999px;
      letter-spacing: 0.02em;
    }

    /* Toolbar */
    .kb-toolbar { padding: 28px 16px 0; }
    .kb-search-wrap { max-width: 560px; }
    .kb-search-icon {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
    }
    .kb-search-input {
      width: 100%;
      height: 44px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 0 42px 0 40px;
      background: #fff;
      font-size: 0.875rem;
      color: #1f2937;
      font-family: inherit;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .kb-search-input::placeholder { color: #9ca3af; }
    .kb-search-input:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .kb-search-clear {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      border: 0;
      background: transparent;
      color: #6b7280;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .kb-search-clear:hover { background: #f3f4f6; color: #1f2937; }

    /* Section */
    .kb-section { padding: 28px 16px 56px; }
    .kb-section-title {
      font-size: 1.0625rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .kb-section-meta {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6b7280;
    }

    /* Article cards */
    .kb-article-card {
      display: block;
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 14px 16px;
      cursor: pointer;
      transition: border-color 0.15s ease, box-shadow 0.2s ease, transform 0.15s ease;
    }
    .kb-article-card:hover {
      border-color: #c7d2fe;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 6px 14px rgba(79, 70, 229, 0.08);
      transform: translateY(-1px);
    }
    .kb-article-card:focus-visible {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.18);
    }
    .kb-article-icon {
      width: 36px;
      height: 36px;
      border-radius: 9px;
      background: #eef2ff;
      color: #4f46e5;
      flex-shrink: 0;
      transition: background 0.15s ease;
    }
    .kb-article-card:hover .kb-article-icon { background: #e0e7ff; }
    .kb-article-card-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .kb-article-card-desc {
      font-size: 0.8125rem;
      color: #6b7280;
      line-height: 1.5;
    }
    .kb-article-card-meta {
      font-size: 0.6875rem;
      font-weight: 500;
      color: #9ca3af;
      letter-spacing: 0.02em;
    }

    /* Icon button */
    .kb-icon-btn {
      width: 32px;
      height: 32px;
      border: 1px solid #e5e7eb;
      background: #fff;
      color: #4b5563;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
      transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.2s ease;
    }
    .kb-article-card:hover .kb-icon-btn {
      background: #eef2ff;
      border-color: #c7d2fe;
      color: #4f46e5;
    }
    .kb-icon-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05);
    }
    .kb-icon-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
    }

    /* Empty */
    .kb-empty {
      border: 1px dashed #e5e5e5;
      border-radius: 12px;
      background: #fff;
      padding: 56px 24px;
    }
    .kb-empty-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: #fafafa;
      border: 1px solid #ececec;
      color: #9ca3af;
    }
    .kb-empty-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 4px;
    }
    .kb-empty-text {
      font-size: 0.8125rem;
      color: #6b7280;
    }

    /* Skeleton */
    .kb-article-card-skel {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 14px 16px;
    }
    .kb-skel {
      background: linear-gradient(90deg, #eef2f7 0%, #f3f4f6 50%, #eef2f7 100%);
      background-size: 200% 100%;
      animation: kb-shimmer 1.4s ease-in-out infinite;
    }
    @keyframes kb-shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
  `}</style>
);

export default ArticleList;
